import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Markdown from "markdown-to-jsx";
import { palette } from "../../../../assets/styles/Palette";
import { Body1, Body3 } from "../../../../assets/styles/Typography";
import { FormBox, CustomTextarea } from "../../../../assets/styles/InputStyle";

const MoleculeEditableMarkdown = ({
  markdown = [],
  title = [],
  onSave,
  onRegenerate,
  onEditingChange,
  onCancel,
  maxLines = 12, // 단일 값이면 두 섹션 모두 동일하게 적용
  maxChars = 300, // 단일 값이면 두 섹션 모두 동일하게 적용
  showRegenerateButton = true,
  editableTitle = false, // 타이틀 편집 가능 여부
  initialEditing = false, // 초기 편집 모드 활성화 여부
  placeholder = {
    title: "타이틀을 입력하세요",
    section1: "마크다운 형식으로 입력해주세요",
    section2: "마크다운 형식으로 입력해주세요",
  },
}) => {
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [editingMarkdown, setEditingMarkdown] = useState(["", ""]);
  const [editingTitle, setEditingTitle] = useState([""]);

  // 초기 편집 모드 활성화
  useEffect(() => {
    if (initialEditing) {
      setEditingMarkdown([
        markdown[0] || "",
        markdown[1] || "",
      ]);
      setEditingTitle([
        title[0] || "",
      ]);
      setIsEditing(true);
      if (onEditingChange) {
        onEditingChange(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditing]);

  // 배열인지 단일 값인지 확인하여 각 섹션의 제한값 결정
  const getMaxLines = (index) => {
    return Array.isArray(maxLines) ? maxLines[index] : maxLines;
  };

  const getMaxChars = (index) => {
    return Array.isArray(maxChars) ? maxChars[index] : maxChars;
  };

  const handleStartEdit = () => {
    setEditingMarkdown([
      markdown[0] || "",
      markdown[1] || "",
    ]);
    setEditingTitle([
      title[0] || "",
    ]);
    setIsEditing(true);
    if (onEditingChange) {
      onEditingChange(true);
    }
  };

  const handleSave = () => {
    if (onSave) {
      if (editableTitle) {
        // 타이틀도 함께 전달 - 배열 형태로 보장
        onSave({
          markdown: editingMarkdown,
          title: Array.isArray(editingTitle) ? editingTitle : [editingTitle || ""],
        });
      } else {
        // 기존 방식 (마크다운만 전달)
        onSave(editingMarkdown);
      }
    }
    setIsEditing(false);
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingMarkdown(["", ""]);
    setEditingTitle([""]);
    if (onEditingChange) {
      onEditingChange(false);
    }
    if (onCancel) {
      onCancel();
    }
  };

  const handleTitleChange = (index, value) => {
    const newTitle = [...editingTitle];
    newTitle[index] = value;
    setEditingTitle(newTitle);
  };

  const handleMarkdownChange = (index, value) => {
    // 줄 수 체크
    const lines = value.split('\n');
    const sectionMaxLines = getMaxLines(index);
    if (lines.length > sectionMaxLines) {
      return;
    }
    
    // 총 글자수 체크
    const sectionMaxChars = getMaxChars(index);
    if (value.length > sectionMaxChars) {
      return;
    }
    
    const newMarkdown = [...editingMarkdown];
    newMarkdown[index] = value;
    setEditingMarkdown(newMarkdown);
  };

  return (
    <ContentConceptBox>
      <ContentConceptHeader>
        <ConceptTitle></ConceptTitle>
        <div style={{ display: "flex", gap: "12px" }}>
          {!isEditing ? (
            <>
              <RegenerateButton onClick={handleStartEdit}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.3333 2.00004C11.5084 1.82493 11.7163 1.68606 11.9442 1.59128C12.1722 1.4965 12.4154 1.44775 12.6667 1.44775C12.9179 1.44775 13.1611 1.4965 13.3891 1.59128C13.617 1.68606 13.8249 1.82493 14 2.00004C14.1751 2.17515 14.314 2.38306 14.4088 2.61101C14.5036 2.83896 14.5523 3.08218 14.5523 3.33337C14.5523 3.58456 14.5036 3.82778 14.4088 4.05573C14.314 4.28368 14.1751 4.49159 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z"
                    stroke="#226FFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>수정</span>
              </RegenerateButton>
              {showRegenerateButton && (
                <RegenerateButton onClick={onRegenerate}>
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.93569 1.10238C6.73825 3.72605 4.44413 4.92239 3.01117 5.27398C2.81081 5.32314 2.66211 5.49758 2.66211 5.70389C2.66211 5.93696 2.85208 6.12328 3.08346 6.15135C5.21146 6.40957 6.69369 8.57558 6.91937 9.99338C6.95039 10.1882 7.10929 10.3482 7.30661 10.3482C7.50393 10.3482 7.66218 10.1869 7.67846 9.99024C7.88338 7.51524 10.2016 6.46546 11.655 6.18303C11.8603 6.14314 12.0161 5.96846 12.0161 5.75933C12.0161 5.53126 11.8294 5.34842 11.6022 5.32934C8.87385 5.10025 7.89142 2.61312 7.69599 1.07933C7.67136 0.886049 7.51151 0.734375 7.31667 0.734375C7.1138 0.734375 6.95091 0.900081 6.93569 1.10238Z"
                      fill="#226FFF"
                    />
                    <path
                      d="M1.88689 8.77442C1.80044 9.92325 0.795916 10.4471 0.168467 10.601C0.0807353 10.6226 0.015625 10.6989 0.015625 10.7893C0.015625 10.8913 0.098793 10.9729 0.200148 10.9849C1.13514 11.0952 1.78541 12.0247 1.88075 12.6673C1.89343 12.7528 1.96291 12.8229 2.04931 12.8229C2.13571 12.8229 2.205 12.7522 2.21213 12.6661C2.30186 11.5824 3.31694 11.1227 3.95333 10.9991C4.04322 10.9816 4.11144 10.9051 4.11144 10.8136C4.11144 10.7137 4.02971 10.6336 3.9302 10.6253C2.73555 10.525 2.30538 9.43593 2.21981 8.76433C2.20902 8.67969 2.13903 8.61328 2.05371 8.61328C1.96488 8.61328 1.89356 8.68584 1.88689 8.77442Z"
                      fill="#226FFF"
                    />
                  </svg>
                  <span>다시 생성</span>
                </RegenerateButton>
              )}
            </>
          ) : (
            <>
              <RegenerateButton onClick={handleSave}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3333 4L6 11.3333L2.66667 8"
                    stroke="#226FFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>저장</span>
              </RegenerateButton>
              <RegenerateButton
                onClick={handleCancel}
                style={{ color: "#666666" }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#666666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span style={{ color: "#666666" }}>취소</span>
              </RegenerateButton>
            </>
          )}
        </div>
      </ContentConceptHeader>
      <ContentConceptBody>
        {isEditing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
            {/* 타이틀 편집 영역 */}
            {editableTitle && (
              <div style={{ display: "flex", flexDirection: "row", gap: "16px", width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <FormBox Large>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <Label>타이틀</Label>
                    </div>
                    <CustomTextarea
                      Edit
                      rows={1}
                      placeholder={placeholder?.title || "타이틀을 입력하세요"}
                      value={editingTitle[0] || ""}
                      onChange={(e) => handleTitleChange(0, e.target.value)}
                      status="valid"
                    />
                  </FormBox>
                </div>
              </div>
            )}
            
            {/* 섹션 편집 영역 */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <FormBox Large>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <Label>첫 번째 섹션</Label>
                    <Body3 color="gray600" style={{ fontSize: "14px" }}>
                      {(editingMarkdown[0] || "").length} / {getMaxChars(0)}자 (최대 {getMaxLines(0)}줄)
                    </Body3>
                  </div>
                  <CustomTextarea
                    Edit
                    rows={getMaxLines(0)}
                    placeholder={placeholder?.section1 || `마크다운 형식으로 입력해주세요 (최대 ${getMaxChars(0)}자, ${getMaxLines(0)}줄)`}
                    value={editingMarkdown[0] || ""}
                    onChange={(e) => handleMarkdownChange(0, e.target.value)}
                    status="valid"
                  />
                </FormBox>
              </div>
              <div style={{ flex: 1 }}>
                <FormBox Large>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <Label>두 번째 섹션</Label>
                    <Body3 color="gray600" style={{ fontSize: "14px" }}>
                      {(editingMarkdown[1] || "").length} / {getMaxChars(1)}자 (최대 {getMaxLines(1)}줄)
                    </Body3>
                  </div>
                  <CustomTextarea
                    Edit
                    rows={getMaxLines(1)}
                    placeholder={placeholder?.section2 || `마크다운 형식으로 입력해주세요 (최대 ${getMaxChars(1)}자, ${getMaxLines(1)}줄)`}
                    value={editingMarkdown[1] || ""}
                    onChange={(e) => handleMarkdownChange(1, e.target.value)}
                    status="valid"
                  />
                </FormBox>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="markdown-body core-value-section"
            style={{
              color: palette.gray800,
              textAlign: "left",
            }}
          >
            <h1
              style={{
                fontSize: "40px",
                fontWeight: "700",
                marginBottom: "20px",
              }}
            >
              {title.length > 0 && title[0]
                ? title[0]
                : ""}
            </h1>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <Markdown>{markdown[0] || ""}</Markdown>
              </div>
              <div style={{ flex: 1 }}>
                <Markdown>{markdown[1] || ""}</Markdown>
              </div>
            </div>
          </div>
        )}
      </ContentConceptBody>
    </ContentConceptBox>
  );
};

export default MoleculeEditableMarkdown;

const ContentConceptBox = styled.div`
  border: 1px solid ${palette.outlineGray};
  border-radius: 10px;
  min-width: 820px;
  overflow: hidden;
`;

const ContentConceptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 8px 20px;
  background: #f2f6ff;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom: none;
`;

const ConceptTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #090f15;
  line-height: 24px;
  font-family: Pretendard;
`;

const RegenerateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: transparent;
  color: #226fff;
  border: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 400;
  font-family: Pretendard;
  line-height: 22.5px;

  span {
    color: #226fff;
  }

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentConceptBody = styled.div`
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  .markdown-body {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 0px;
      margin-bottom: 12px;
      color: #090f15;
      font-weight: 400;
      font-family: Pretendard;
    }
    p,
    li,
    span {
      line-height: 1.55;
      color: #090f15;
      font-size: 19px;
      font-weight: 400;
      font-family: Pretendard;
      line-height: 28.5px;
      white-space: pre-line;
    }
    p {
      margin-bottom: 8px;
      white-space: pre-line;
    }
    h1 {
      font-size: 24px;
    }
    h2 {
      font-size: 20px;
    }
    h3 {
      font-size: 18px;
    }
  }
  .core-value-section h2:nth-of-type(4) ~ p:last-of-type {
    margin-bottom: 12px !important;
  }

  h2:nth-of-type(4) ~ h2:nth-of-type(5) {
    margin-top: 20px;
  }
`;

const Label = styled.label`
  color: #666666;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 1.55;
  text-align: left;
`;

